/**
 * @voltxampere/web — ERC ↔ realisticMode bridge (Sprint 60 / S56c closure)
 *
 * The S59 ERC overheating rule is opt-in via
 * `setOverheatingRuleConfig({ enabled, ambientTempC })`. This module
 * watches the web-side `realisticMode` store and threads its state
 * into the engine rule so the user-facing toggle controls whether
 * the rule fires.
 *
 * S56c originally deferred this bridge as part of the DC-aware ERC
 * web closure backlog; S60 closes it pre-launch so the
 * realisticMode toggle is end-to-end functional (toggle on → engine
 * rule active; toggle off → engine rule silent).
 *
 * The bridge does NOT itself run runERC — that's the existing
 * Sprint 56c auto-run hook's job. This module only keeps the engine
 * rule's config in sync with the user's toggle choice.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { effect } from "@preact/signals-core";
import { setOverheatingRuleConfig } from "@voltxampere/engine";

import { ambientTempC, realisticModeEnabled } from "./realistic-mode.js";

let activeUnsubscribe: (() => void) | null = null;

/**
 * Activate the bridge. Re-applies the engine config every time the
 * realisticMode store changes. Returns a teardown.
 */
export function bootErcRealisticBridge(): () => void {
  if (activeUnsubscribe) return activeUnsubscribe;
  const dispose = effect(() => {
    const enabled = realisticModeEnabled.value;
    const ambient = ambientTempC.value;
    setOverheatingRuleConfig({ enabled, ambientTempC: ambient });
  });
  activeUnsubscribe = (): void => {
    dispose();
    activeUnsubscribe = null;
  };
  return activeUnsubscribe;
}

/** Test helper — cold reset. */
export function resetErcRealisticBridgeForTests(): void {
  activeUnsubscribe?.();
  activeUnsubscribe = null;
  setOverheatingRuleConfig({ enabled: false, ambientTempC: 25 });
}
